import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ReactComponent as MoreHorizIcon } from "../../../../assets/images/menukb.svg";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ReactComponent as EyeOpen } from "../../../../assets/images/eyeopen.svg";
import { ReactComponent as EyeClose } from "../../../../assets/images/eyeclose.svg";
import { getArticleStatusStyle } from "../../../../utils/articlesHelpers";
import TablePagination from "@mui/material/TablePagination";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { NewBadge } from "../../../../utils";
import { ReactComponent as Check } from "../../../../assets/images/check.svg";
import { ReactComponent as Link } from "../../../../assets/images/link.svg";
import ArticleActionPopup from "./ArticleActionPopup";
import { Tooltip } from "@mui/material";
import NoDataFound from "../../../dashboardcomponents/Traffic/NoDataFound";
import CheckIcon from '@mui/icons-material/Check';
import { buildExternalKbUrl, selectBusinessSlug } from "../../../../utils/kbPublicUrl";


function VisibilityChip({ visibility }) {
  const isPublic = (visibility || "Public").toLowerCase() === "public";
  return (
    <Box sx={{
      width: isPublic ? '80px' : '88px',
      height: '24px',
      borderRadius: '5px',
      border: '1px solid #D5DCE1',
      background: isPublic ? '#FFF' : '#F9FAFA',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      color: '#405261',
      fontFamily: 'Inter',
      fontSize: '12px',
      fontWeight: 500,
    }}>
      {isPublic ? (
        <EyeOpen />
      ) : (
        <EyeClose />
      )}
      {isPublic ? 'Public' : 'Internal'}
    </Box>
  );
}

export default function ArticlesTable({
  rows,
  selected,
  setSelected,
  onAction,
  page,
  rowsPerPage,
  onChangePage,
  totalCount // New prop for server-side total count
}) {
  const navigate = useNavigate();


  const [ellipsisStates, setEllipsisStates] = useState({});
  const titleRefs = useRef({});
  const categoryRefs = useRef({});

  useEffect(() => {
    const checkAllEllipsis = () => {
      const newStates = {};
      visibleRows.forEach(row => {
        const titleElement = titleRefs.current[row.id];
        const categoryElement = categoryRefs.current[`${row.id}-category`];

        if (titleElement) {
          newStates[`${row.id}-title`] = titleElement.scrollWidth > titleElement.clientWidth;
        }
        if (categoryElement) {
          newStates[`${row.id}-category`] = categoryElement.scrollWidth > categoryElement.clientWidth;
        }
      });
      setEllipsisStates(newStates);
    };

    checkAllEllipsis();
    window.addEventListener('resize', checkAllEllipsis);

    return () => {
      window.removeEventListener('resize', checkAllEllipsis);
    };
  }, [visibleRows]);
  const BlueRoundedCheckbox = styled(Checkbox)(({ theme }) => ({
    padding: "0px",
    width: "16px",
    height: "16px",
    margin: "0 auto", // Center horizontally
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&.MuiCheckbox-root": { color: "transparent" },
    "& .MuiSvgIcon-root": {
      width: "16px",
      height: "16px",
      borderRadius: "4px",
      border: "1px solid #D5DCE1",
      backgroundColor: "#FFF",
      boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
      path: { display: "none" },
    },
    "&.Mui-checked .MuiSvgIcon-root": {
      border: "1px solid #148DFD",
      backgroundColor: "#FFF"
    },
    "&:hover .MuiSvgIcon-root": { border: "1px solid #148DFD" },
    "&:hover": { backgroundColor: "transparent" },
  }));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [menuRowId, setMenuRowId] = React.useState(null);
  const open = Boolean(anchorEl);
  const [popupType, setPopupType] = React.useState(null);
  const [popupRowId, setPopupRowId] = React.useState(null);
  const [copiedRowId, setCopiedRowId] = React.useState(null);


  // Determine if we are using server-side data (totalCount provided means yes)
  const isServerSide = typeof totalCount === 'number';

  const visibleRows = React.useMemo(
    () => {
      if (isServerSide) return rows;
      return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    },
    [rows, page, rowsPerPage, isServerSide]
  );

  const visibleIds = React.useMemo(() => visibleRows.map(r => r.id), [visibleRows]);
  const selectedOnPage = selected.filter(id => visibleIds.includes(id));
  const allSelected = visibleRows.length > 0 && selectedOnPage.length === visibleRows.length;
  const someSelected = selectedOnPage.length > 0 && selectedOnPage.length < visibleRows.length;
  const userData = useSelector((state) => state.loginToken);
  const kbRole = userData?.response?.user?.kbRole?.role;
  const businessSlug = useSelector(selectBusinessSlug);

  const displayCount = isServerSide ? totalCount : rows.length;

  const toggleAll = (e) => {
    if (e.target.checked) {
      setSelected(prev => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      setSelected(prev => prev.filter(id => !visibleIds.includes(id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleMenuOpen = (e, id) => {
    setAnchorEl(e.currentTarget);
    setMenuRowId(id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  // New popup open functions
  const openArchivePopup = () => {
    setPopupType('archive');
    setPopupRowId(menuRowId);
    handleMenuClose();
  };
  const openDeletePopup = () => {
    setPopupType('delete');
    setPopupRowId(menuRowId);
    handleMenuClose();
  };

  // Popup close/reset
  const closePopup = () => {
    setPopupType(null);
    setPopupRowId(null);
  }

  // Confirm actions
  const handleConfirm = () => {
    if (popupType && popupRowId) {
      onAction(popupType, popupRowId);
    }
    closePopup();
  };

  const call = (type) => {
    onAction(type, menuRowId);
    handleMenuClose();
  };

  const currentRow = visibleRows.find(r => r.id === menuRowId) || {};

  return (
    <Box sx={{ mt: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{
        flex: 1,
        overflowX: 'auto',
        overflowY: 'auto',
        minHeight: 0
      }}>
        {/* Header row with responsive grid */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "24px minmax(200px,1fr) 120px 140px 150px 100px 100px 48px",
            sm: "24px minmax(250px,1fr) 150px 150px 180px 120px 110px 48px",
            md: "24px minmax(300px,1fr) 170px 160px 180px 140px 120px 48px",
            lg: "24px minmax(356px,1fr) 142px 153px 133px 118px 104px 48px"
          },
          gap: 0, // remove column gap between checkbox and Title
          alignItems: "center",
          px: 0,
          height: 48,
          zIndex: 1, // Add this line
          position: 'sticky',
          top: 0,
          background: '#fff',
          minWidth: 'fit-content',
        }}>
          <BlueRoundedCheckbox
            color="primary"
            indeterminate={someSelected}
            checked={allSelected}
            onChange={toggleAll}
            checkedIcon={
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: '4px',
                border: '1px solid #148DFD',
                backgroundColor: '#FFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check style={{

                  color: '#148DFD'
                }} />
              </div>
            }
            sx={{ mb: '-15px', m: 0 }}
          />
          <Typography sx={{ color: "#405261", fontSize: 12, fontWeight: 400 }}>Title</Typography>
          <Typography sx={{ color: "#405261", fontSize: 12, fontWeight: 400 }}>Status</Typography>
          <Typography sx={{ color: "#405261", fontSize: 12, fontWeight: 400 }}>Created by</Typography>
          <Typography sx={{ color: "#405261", fontSize: 12, fontWeight: 400 }}>Category</Typography>
          <Typography sx={{ color: "#405261", fontSize: 12, fontWeight: 400 }}>Visibility</Typography>
          <Typography sx={{ color: "#405261", fontSize: 12, fontWeight: 400, pr: 1 }}>Last Updated</Typography>
          <Box />
        </Box>

        {visibleRows.length === 0 ? (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <NoDataFound containerStyle={{ height: '59vh' }} isForKb={true} title="No articles found" description="This segment is empty. Try another one or come back later" />
          </Box>
        ) : visibleRows.map((row, idx) => (
          <Box
            key={row.id}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "24px minmax(200px,1fr) 120px 140px 150px 100px 100px 48px",
                sm: "24px minmax(250px,1fr) 150px 150px 180px 120px 110px 48px",
                md: "24px minmax(300px,1fr) 170px 160px 180px 140px 120px 48px",
                lg: "24px minmax(356px,1fr) 142px 153px 133px 118px 104px 48px",
              },
              gap: 0, // align columns flush (remove 8px gap)
              alignItems: "center",
              px: 0,
              height: 64,
              borderTop: "1px solid #EEF2F6",
              minWidth: 'fit-content',
              borderBottom: idx === visibleRows.length - 1 && '1px solid #EEF2F6',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#F7F8F9'
                ,
              }
            }}
            onClick={(e) => {
              // Prevent navigation if clicking on checkbox or action button
              if (e.target.closest('.MuiCheckbox-root') || e.target.closest('.MuiIconButton-root')) {
                return;
              }
              navigate(`/kb/article/${row.id}`);
            }}
          >
            <BlueRoundedCheckbox
              size="small"
              checked={selected.includes(row.id)}
              onChange={() => toggleOne(row.id)}
              sx={{ m: 0 }}
              checkedIcon={
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '4px',
                  border: '1px solid #148DFD',
                  backgroundColor: '#FFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Check sx={{ fontSize: 14, color: '#148DFD' }} />
                </div>
              }
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                overflow: "hidden",
              }}
            >
              {ellipsisStates[row.id] ? (
                <Tooltip title={row.title} arrow>
                  <Typography
                    ref={(el) => {
                      if (el) {
                        titleRefs.current[row.id] = el;
                      }
                    }}
                    sx={{
                      color: "#061522",
                      fontSize: 14,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.title}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  ref={(el) => {
                    if (el) {
                      titleRefs.current[row.id] = el;
                    }
                  }}
                  sx={{
                    color: "#061522",
                    fontSize: 14,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.title}
                </Typography>
              )}

              {
                row.visibility?.toLowerCase() === 'public' &&
                !['pendingreview', 'draft', 'new'].includes(row.status?.toLowerCase()) && (
                  <Tooltip title={copiedRowId === row.id ? "Copied!" : "Copy public link"} arrow>
                    <Box
                      component="div"
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = buildExternalKbUrl({
                          slug: businessSlug,
                          articleId: row.id,
                        });
                        if (!url) return;
                        navigator.clipboard.writeText(url);
                        setCopiedRowId(row.id);
                        setTimeout(() => setCopiedRowId(null), 2000);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.7
                        }
                      }}
                    >
                      {copiedRowId === row.id ? (
                        <CheckIcon sx={{ fontSize: 20, marginRight: '8px', color: '#148DFD' }} />
                      ) : (
                        <Link style={{ minWidth: 16, minHeight: 16, marginRight: '8px' }} />
                      )}
                    </Box>
                  </Tooltip>
                )}
              {row.status?.toLowerCase() === 'new' && <NewBadge />}
            </Box>
            {
              row.status?.toLowerCase() === 'new' ? (
                <Box />
              ) : (
                <Box sx={getArticleStatusStyle(row.status)}>{row.status?.toLowerCase() === 'pendingreview' ? 'Pending Review' : row.status}</Box>
              )
            }

            < Typography
              sx={{
                color: "#405261",
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.createdBy}
            </Typography>

            {ellipsisStates[`${row.id}-category`] ? (
              <Tooltip title={row.category} arrow>
                <Typography
                  ref={(el) => {
                    if (el) {
                      categoryRefs.current[`${row.id}-category`] = el;
                    }
                  }}
                  sx={{
                    color: "#405261",
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    width: '90%'
                  }}
                >
                  {row.category}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                ref={(el) => {
                  if (el) {
                    categoryRefs.current[`${row.id}-category`] = el;
                  }
                }}
                sx={{
                  color: "#405261",
                  fontSize: 14,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  width: '90%'
                }}
              >
                {row.category}
              </Typography>
            )}

            <VisibilityChip visibility={row.visibility} />

            <Typography sx={{ color: '#405261', fontSize: 14, fontWeight: 400 }}>
              {row.updated}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <IconButton onClick={(e) => handleMenuOpen(e, row.id)} size="small" sx={{ ml: 1 }}>
                <MoreHorizIcon />
              </IconButton>
            </Box>
          </Box>
        ))
        }

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          elevation={0}
          transitionDuration={0}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          MenuListProps={{ disablePadding: true }}
          PaperProps={{
            sx: {
              width: '184px',
              borderRadius: '8px',
              border: '1px solid #D5DCE1',
              background: '#FFF',
              boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
              mt: '4px',
              zIndex: 1302,
              overflow: 'hidden',
              paddingTop: '8px',
              paddingBottom: '8px',
              '& .MuiMenuItem-root': {
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 'normal',
                px: '16px',
                '&:hover': { borderRadius: '8px', background: '#F9FAFA' },
              },
              '& li:nth-of-type(1)': { color: '#405261 !important' },
              '& li:nth-of-type(2)': { color: '#405261 !important' },
              '& li:nth-of-type(3)': { color: '#405261 !important' },
              '& li:nth-of-type(4)': { color: '#405261 !important' },
              '& li:nth-of-type(5)': { color: '#405261 !important' },
              '& li:nth-of-type(6)': { color: '#FF4D4F !important' },
            },
          }}
        >
          <MenuItem onClick={() => call('publish')} disabled={(currentRow?.status || '').toLowerCase() === 'published' || (kbRole || '').toLowerCase() === 'viewer' || (kbRole || '').toLowerCase() === 'contributor'}
            sx={{ px: '16px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#405261' }}>
            <Typography>Published</Typography>
          </MenuItem>
          <MenuItem onClick={() => call('unpublish')} disabled={(currentRow?.status || '').toLowerCase() !== 'published' || (kbRole || '').toLowerCase() === 'viewer' || (kbRole || '').toLowerCase() === 'contributor'}
            sx={{ px: '16px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#405261' }}>
            <Typography>Unpublished</Typography>
          </MenuItem>
          <MenuItem onClick={() => call('public')} disabled={(currentRow?.visibility || '').toLowerCase() === 'public' || (kbRole || '').toLowerCase() === 'viewer' }
            sx={{ px: '16px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#405261' }}>
            <Typography>Public</Typography>
          </MenuItem>
          <MenuItem onClick={() => call('internal')} disabled={(currentRow?.visibility || '').toLowerCase() === 'internal' || (kbRole || '').toLowerCase() === 'viewer' }
            sx={{ px: '16px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#405261' }}>
            <Typography>Internal</Typography>
          </MenuItem>
          <MenuItem
            onClick={openArchivePopup}
            disabled={
              (currentRow?.status || '').toLowerCase() === 'archived' ||
              (kbRole || '').toLowerCase() === 'contributor' ||
              (kbRole || '').toLowerCase() === 'viewer'
            }
            sx={{ px: '16px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#405261' }}
          >
            <Typography>Archive</Typography>
          </MenuItem>
          <MenuItem onClick={openDeletePopup} disabled={(kbRole || '').toLowerCase() === 'contributor' || (kbRole || '').toLowerCase() === 'viewer'}
            sx={{ px: '16px', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: '#FF4D4F' }}>
            <Typography>Delete</Typography>
          </MenuItem>
        </Menu>
      </Box >

      {/* Sticky Pagination Footer */}

      {displayCount > rowsPerPage && (
        <Box sx={{
          width: '100%',
          background: '#fff',
          borderTop: '1px solid #E5E9EC',
          padding: '16px 0px',
          position: 'sticky',
          bottom: 0,
          left: 0,
        }}>
          <TablePagination
            component="div"
            count={displayCount}
            page={page}
            onPageChange={(_, newPage) => onChangePage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to }) => (
              <Typography sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400 }}>
                Articles {from}-{to}
              </Typography>
            )}
            ActionsComponent={(props) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 'auto', flexWrap: 'wrap' }}>
                <Button
                  onClick={(e) => props.onPageChange(e, props.page - 1)}
                  disabled={props.page === 0}
                  sx={{
                    width: { xs: '100px', sm: '128px' },
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #D5DCE1',
                    background: '#FFF',
                    boxShadow: '0px 1px 2px 0px rgba(6, 21, 34, 0.05)',
                    color: '#405261',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:not(.Mui-disabled):hover': { backgroundColor: '#FFFFFF', borderColor: '#C5CED6' },
                    '&.Mui-disabled': { color: '#93A1B0', border: '1px solid #C5CED6', bgcolor: '#F9FAFA' },
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={(e) => props.onPageChange(e, props.page + 1)}
                  disabled={props.page >= Math.ceil(props.count / rowsPerPage) - 1}
                  sx={{
                    width: { xs: '80px', sm: '88px' },
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #D5DCE1',
                    background: '#FFFFFF',
                    boxShadow: '0px 1px 2px 0px rgba(6, 21, 34, 0.05)',
                    color: '#405261',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:not(.Mui-disabled):hover': { backgroundColor: '#FFFFFF', borderColor: '#C5CED6' },
                    '&.Mui-disabled': { color: '#93A1B0', border: '1px solid #C5CED6', bgcolor: '#F9FAFA' },
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
            sx={{
              '.MuiTablePagination-toolbar': {
                padding: 0,
                minHeight: 'unset',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              },
              '.MuiTablePagination-spacer': { display: 'none' },
              '.MuiTablePagination-selectLabel': { display: 'none' },
              '.MuiTablePagination-select': { display: 'none' },
              '.MuiTablePagination-selectIcon': { display: 'none' },
            }}
          />
        </Box>
      )
      }

      {
        popupType && (
          <ArticleActionPopup
            title={popupType === 'archive' ? 'Move to Archive' : 'Delete Article'}
            description={popupType === 'archive'
              ? 'This action will move the article to archive. Continue?'
              : 'Are you sure you want to delete this article? This action cannot be undone.'}
            confirmText={popupType === 'archive' ? 'Move' : 'Delete'}
            cancelText="Cancel"
            onConfirm={handleConfirm}
            onCancel={closePopup}
            popupType={popupType}
          />
        )
      }
    </Box >
  );
}